import { Button } from "@/components/ui/button";
import {
    ArDateFormField,
    ArFileFormField,
    ArForm,
    ArFormField,
    ArNumberFormField,
    ArSelectFormField,
} from "@/v2/components/ui/form";
import {
    CreateInvoiceSchema,
    InvoiceFormSchema,
    UpdateInvoiceSchema,
} from "@/v2/schema/invoice";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import LoadingAnimation from "../../ui/LoadingAnimation";
import FindOneInvoice from "@/v2/api/invoice/FindOne";
import CreateInvoice from "@/v2/api/invoice/Create";
import { ClientComboBox, SupplierComboBox } from "../../ui/combo-box";
import UpdateInvoice from "@/v2/api/invoice/Update";

// const DUE_PERIOD_OPTIONS_RECEIVABLE = [
//     { value: "5_DAYS", label: "5 Days Period" },
//     { value: "10_DAYS", label: "10 Days Period" },
//     { value: "15_DAYS", label: "15 Days Period" },
//     { value: "20_DAYS", label: "20 Days Period" },
//     { value: "30_DAYS", label: "30 Days Period" },
//     { value: "45_DAYS", label: "45 Days Period" },
//     { value: "60_DAYS", label: "60 Days Period" },
//     { value: "90_DAYS", label: "90 Days Period" },
// ];

// const DUE_PERIOD_OPTIONS_PAYABLE = [
//     { value: "45_DAYS", label: "45 Days Period" },
//     { value: "60_DAYS", label: "60 Days Period" },
//     { value: "90_DAYS", label: "90 Days Period" },
// ];
//
export default function InvoiceForm({
    id,
    closeOnSave,
    type,
}: {
    id?: string;
    closeOnSave: () => void;
    type: "RECEIVABLE_INVOICE" | "PAYABLE_INVOICE";
}) {
    const queryClient = useQueryClient();

    const query = useQuery({
        queryKey: ["invoice", id],
        queryFn: () => FindOneInvoice(id!),
        enabled: !!id,
    });

    const form = useForm<InvoiceFormSchema>({
        resolver: zodResolver(InvoiceFormSchema),
        defaultValues: {
            client_id: type === "RECEIVABLE_INVOICE" ? "" : "",
            supplier_id: type === "PAYABLE_INVOICE" ? "" : null,
            completed: "false",
            invoice_number: "",
            type: type,
            invoice_amount: 0,
            invoice_vat: 15,
            invoice_date: "",
            invoice_due_date: "",
            invoice_paid: null,
            invoice_paid_amount: 0,
            reference_number: "",
            file: null,
            existing_invoice_receipt: [],
            months_and_year_against_time_sheet: "",
            remarks: "",
        },
    });

    const { reset } = form;

    useEffect(() => {
        if (query.data) {
            reset({
                completed: String(query.data?.completed) ?? "false",
                invoice_number: query.data?.invoice_number || "",
                type: query.data?.type,
                invoice_amount: query.data?.invoice_amount || 0,
                invoice_vat: query.data?.invoice_vat || 15,
                invoice_date: query.data?.invoice_date || "",
                invoice_due_date: query.data?.invoice_due_date || "",
                invoice_paid: query.data?.invoice_paid || null,
                invoice_paid_amount: query.data?.invoice_paid_amount || 0,
                existing_invoice_receipt: query.data?.invoice_receipt || [],
                supplier_id: query.data?.supplier?.id || null,
                client_id: query.data?.client?.id || null,
                remarks: query?.data?.remarks || "",
                reference_number: query?.data?.reference_number || "",
                months_and_year_against_time_sheet:
                    query?.data?.months_and_year_against_time_sheet || "",
            });
        }
    }, [query.data, reset]);

    const createMutation = useMutation({
        mutationFn: (data: CreateInvoiceSchema) => CreateInvoice(data),
    });

    const editMutation = useMutation({
        mutationFn: (data: UpdateInvoiceSchema) =>
            UpdateInvoice({ id: id!, data }),
    });

    const handleFormSubmit = (formData: InvoiceFormSchema) => {
        if (id) {
            const updateData: UpdateInvoiceSchema = {
                client_id: formData.client_id,
                supplier_id: formData.supplier_id,
                completed: formData.completed,
                invoice_number: formData.invoice_number,
                type: formData.type,
                invoice_amount: formData.invoice_amount,
                invoice_vat: formData.invoice_vat,
                invoice_date: formData.invoice_date,
                invoice_due_period: formData.invoice_due_period,
                invoice_due_date: formData.invoice_due_date,
                invoice_paid: formData.invoice_paid,
                invoice_paid_amount: formData.invoice_paid_amount,
                invoice_receipt: formData.existing_invoice_receipt ?? [],
                file: formData.file || null,
                remarks: formData.remarks,
                months_and_year_against_time_sheet:
                    formData.months_and_year_against_time_sheet ?? "",
                reference_number: formData.reference_number ?? "",
            };

            editMutation.mutate(updateData, {
                onError: (err) => {
                    console.error(err);
                    toast.error("Something went wrong", {
                        description: "Error updating invoice.",
                    });
                },
                onSuccess: () => {
                    toast.success("Invoice updated successfully");
                    queryClient.invalidateQueries({
                        queryKey: ["invoices"],
                    });
                    closeOnSave();
                    reset();
                },
            });
        } else {
            const createData: CreateInvoiceSchema = {
                client_id: formData.client_id,
                supplier_id: formData.supplier_id,
                completed: formData.completed,
                invoice_number: formData.invoice_number,
                type: formData.type,
                invoice_amount: formData.invoice_amount,
                invoice_vat: formData.invoice_vat,
                invoice_date: formData.invoice_date,
                invoice_due_period: formData.invoice_due_period,
                invoice_due_date: formData.invoice_due_date,
                invoice_paid: formData.invoice_paid,
                invoice_paid_amount: formData.invoice_paid_amount,
                invoice_receipt: formData.file || undefined,
                months_and_year_against_time_sheet:
                    formData.months_and_year_against_time_sheet ?? "",
                remarks: formData.remarks,

                reference_number: formData.reference_number ?? "",
            };

            createMutation.mutate(createData, {
                onError: (err) => {
                    console.error(err);
                    toast.error("Something went wrong", {
                        description: "Error creating invoice.",
                    });
                },
                onSuccess: () => {
                    toast.success("Invoice created successfully");
                    queryClient.invalidateQueries({
                        queryKey: ["invoices"],
                    });
                    closeOnSave();
                },
            });
        }
    };
    if (id && query.isLoading) return <LoadingAnimation />;

    if (id && query.isError)
        return (
            <div className="flex items-center justify-center p-8">
                <div className="text-center">
                    <p className="mb-4 text-red-600">Failed to load invoice</p>
                    <Button onClick={closeOnSave}>Go Back</Button>
                </div>
            </div>
        );

    return (
        <ArForm
            form={form}
            formSchema={InvoiceFormSchema}
            onSubmit={handleFormSubmit}
        >
            <div className="space-y-6">
                {/* Identification */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <ArFormField<InvoiceFormSchema>
                        label="Invoice Number"
                        name="invoice_number"
                        placeholder="Enter invoice number"
                    />
                    <ArFormField<InvoiceFormSchema>
                        label="PO Number"
                        name="reference_number"
                        placeholder="Enter PO number"
                    />
                </div>

                {/* Financials */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <ClientComboBox<InvoiceFormSchema>
                        name="client_id"
                        defaultValue={query?.data?.client || null}
                    />

                    {type === "PAYABLE_INVOICE" && (
                        <SupplierComboBox
                            name="supplier_id"
                            defaultValue={query?.data?.supplier || null}
                        />
                    )}

                    <ArNumberFormField<InvoiceFormSchema>
                        label="Invoice Amount"
                        name="invoice_amount"
                        placeholder="Enter amount"
                    />

                    <ArFormField<InvoiceFormSchema>
                        label="Timesheet Month"
                        name="months_and_year_against_time_sheet"
                    />

                    {type === "RECEIVABLE_INVOICE" && (
                        <ArNumberFormField<InvoiceFormSchema>
                            label={
                                type === "RECEIVABLE_INVOICE"
                                    ? "Invoice Recevied Amount"
                                    : "Invoice Paid Amount"
                            }
                            name="invoice_paid_amount"
                            placeholder="Enter paid amount"
                        />
                    )}
                </div>

                {/* Dates */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <ArDateFormField<InvoiceFormSchema>
                        label="Invoice Date"
                        name="invoice_date"
                    />
                    <ArDateFormField<InvoiceFormSchema>
                        label="Invoice Due Date"
                        name="invoice_due_date"
                    />

                    <ArDateFormField<InvoiceFormSchema>
                        label={
                            type === "RECEIVABLE_INVOICE"
                                ? "Recevied Date"
                                : "Paid Date"
                        }
                        name="invoice_paid"
                        showClearButton
                    />
                    <ArSelectFormField<InvoiceFormSchema>
                        label="Status"
                        name="completed"
                        options={[
                            {
                                value: "true",
                                label: `${type === "RECEIVABLE_INVOICE" ? "Recevied" : "Paid"}`,
                            },
                            {
                                value: "false",
                                label: `${type === "RECEIVABLE_INVOICE" ? "Yet to Receive" : "Yet to Pay"}`,
                            },
                        ]}
                    />
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <ArFileFormField<InvoiceFormSchema>
                        label="Invoice Receipt"
                        name="file"
                    />
                    <ArFormField<InvoiceFormSchema>
                        label="Remarks"
                        name="remarks"
                    />
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-2 border-t pt-4">
                    <Button
                        type="submit"
                        className="min-w-[160px]"
                        disabled={
                            createMutation.isPending || editMutation.isPending
                        }
                    >
                        {createMutation.isPending || editMutation.isPending
                            ? "Saving..."
                            : id
                              ? "Update Invoice"
                              : "Create Invoice"}
                    </Button>
                </div>
            </div>
        </ArForm>
    );
}
